import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../../services/api";
import {
  AlertCircle,
  CheckCircle2,
  Loader2,
  Mail,
  MapPin,
  MessageCircle,
  Phone,
  Send,
} from "lucide-react";
import {
  CtaSection,
  PageHero,
  Section,
  btnOutlineDark,
  btnPrimary,
  cardClass,
  cardIconClass,
  fieldClass,
  useAuthActions,
} from "../components/SiteUI";

const FALLBACK = {
  email: "info@gamrent.gm",
  phone: "+220 345 6127",
  whatsapp: "+220 345 6127",
  address: "Opposite Wullinkamma Junction, OIC Road, Sukuta",
  platformName: "Gamrent"
};

const Contact = () => {
  const navigate = useNavigate();
  const { openSignup } = useAuthActions();
  const [settings, setSettings] = useState(FALLBACK);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
  });
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const loadSettings = async () => {
      try {
        const res = await api.get("/system-settings/public");
        setSettings({ ...FALLBACK, ...(res.data?.data || {}) });
      } catch (error) {
        setSettings(FALLBACK);
      }
    };

    loadSettings();
  }, []);

  const handleChange = (e) => {
    if (error) setError("");
    setFormData((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const name = formData.name.trim();
    const email = formData.email.trim();
    const message = formData.message.trim();

    if (!name || !email || !message) {
      setError("Please fill in name, email, and message.");
      return;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError("Please enter a valid email address.");
      return;
    }

    try {
      setLoading(true);
      setError("");
      await api.post("/contact", {
        name,
        email,
        phone: formData.phone.trim(),
        message
      });
      setSubmitted(true);
      setFormData({ name: "", email: "", phone: "", message: "" });
    } catch (err) {
      setError(err.response?.data?.message || "Failed to send message. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-teal-50">
      <PageHero
        eyebrow="Contact"
        title="Get in Touch"
        description="Reach out for product questions, onboarding help, or enterprise support needs."
      />

      <Section>
        <div className="grid lg:grid-cols-[1fr_1.2fr] gap-6">
          <aside className={cardClass}>
            <h2 className="text-xl font-extrabold text-teal-900">Contact Information</h2>
            <ul className="mt-5 space-y-4 text-sm text-teal-800">
              <li className="flex items-start gap-3">
                <span className={cardIconClass} aria-hidden="true"><Mail className="w-5 h-5" /></span>
                <div className="min-w-0">
                  <p className="font-bold text-teal-900">Email</p>
                  <a href={`mailto:${settings.email}`} className="text-teal-700 hover:underline break-words">{settings.email}</a>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <span className={cardIconClass} aria-hidden="true"><Phone className="w-5 h-5" /></span>
                <div className="min-w-0">
                  <p className="font-bold text-teal-900">Phone</p>
                  <a href={`tel:${String(settings.phone || "").replace(/\s/g, "")}`} className="text-teal-700 hover:underline">{settings.phone}</a>
                </div>
              </li>
              {settings.whatsapp ? (
                <li className="flex items-start gap-3">
                  <span className={cardIconClass} aria-hidden="true"><MessageCircle className="w-5 h-5" /></span>
                  <div className="min-w-0">
                    <p className="font-bold text-teal-900">WhatsApp</p>
                    <p>{settings.whatsapp}</p>
                  </div>
                </li>
              ) : null}
              <li className="flex items-start gap-3">
                <span className={cardIconClass} aria-hidden="true"><MapPin className="w-5 h-5" /></span>
                <div className="min-w-0">
                  <p className="font-bold text-teal-900">Address</p>
                  <p>{settings.address}</p>
                </div>
              </li>
            </ul>
          </aside>

          <div className={cardClass}>
            <h2 className="text-xl font-extrabold text-teal-900">Send a Message</h2>

            {submitted ? (
              <div className="mt-6 flex items-start gap-2.5 rounded-lg bg-teal-50 border border-teal-200 p-4 text-teal-800 text-sm">
                <CheckCircle2 className="w-5 h-5 shrink-0 text-teal-700" aria-hidden="true" />
                Message sent successfully. Our team will reach out shortly.
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="mt-6 space-y-4">
                {error ? (
                  <div className="flex items-start gap-2.5 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                    <AlertCircle className="w-5 h-5 shrink-0" aria-hidden="true" />
                    {error}
                  </div>
                ) : null}
                <div className="grid sm:grid-cols-2 gap-4">
                  <input
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Full name"
                    required
                    className={fieldClass}
                  />
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="Email address"
                    required
                    className={fieldClass}
                  />
                </div>

                <input
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="Phone number"
                  className={fieldClass}
                />

                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  placeholder="Tell us how we can help"
                  rows={5}
                  required
                  className={fieldClass}
                />

                <button
                  type="submit"
                  disabled={loading}
                  className={`w-full sm:w-auto ${btnPrimary} gap-2`}
                >
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" /> : <Send className="w-4 h-4" aria-hidden="true" />}
                  {loading ? "Sending..." : "Send Message"}
                </button>
              </form>
            )}
          </div>
        </div>
      </Section>

      <CtaSection
        title="Ready to onboard your portfolio?"
        description={`Create your account, and contact ${settings.platformName || "Gamrent"} admin to activate dashboard access after payment verification. Use your email to log in.`}
      >
        <button onClick={openSignup} className={btnPrimary}>
          Create Account
        </button>
        <button onClick={() => navigate("/pricing")} className={btnOutlineDark}>
          View Pricing
        </button>
      </CtaSection>
    </div>
  );
};

export default Contact;

